import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fieldAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Banknote, Search, Calendar, Star, Loader2, Building2 } from 'lucide-react';

export default function FieldsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadFields();
  }, [selectedDate]);

  const loadFields = async () => {
    try {
      const response = await fieldAPI.getAvailable(selectedDate);
      setFields(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Plätze');
    } finally {
      setLoading(false);
    }
  };

  const filteredFields = fields.filter(f =>
    f.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-tunis-blue">Fußballplätze buchen</h1>
          <p className="text-muted-foreground">Finde und reserviere deinen perfekten Platz</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Platz oder Standort suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
            />
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <Input
              type="date"
              value={selectedDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-[180px] h-12"
            />
          </div>
        </div>

        {/* Fields Grid */}
        {filteredFields.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFields.map(field => (
              <Card key={field.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                <CardContent className="p-0">
                  {/* Field Image Placeholder */}
                  <div className="h-40 bg-gradient-to-br from-tunis-blue to-tunis-teal flex items-center justify-center">
                    <Building2 className="w-16 h-16 text-white/50" />
                  </div>
                  
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-bold text-lg text-tunis-blue">{field.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {field.location}
                      </p>
                    </div>
                    
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {field.description}
                    </p>
                    
                    {field.amenities?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {field.amenities.slice(0, 3).map((a, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{a}</Badge>
                        ))}
                        {field.amenities.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{field.amenities.length - 3}</Badge>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-1">
                        <Banknote className="w-5 h-5 text-tunis-teal" />
                        <span className="text-xl font-bold text-tunis-teal">{field.price_per_hour}</span>
                        <span className="text-sm text-muted-foreground">TND/Std.</span>
                      </div>
                      
                      {field.available_slots_today > 0 ? (
                        <Badge className="bg-green-100 text-green-700">
                          {field.available_slots_today} Slots frei
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Ausgebucht</Badge>
                      )}
                    </div>
                    
                    <Button
                      className="w-full btn-primary"
                      onClick={() => navigate(`/book/${field.id}`)}
                      disabled={field.available_slots_today === 0}
                    >
                      {field.available_slots_today > 0 ? 'Jetzt buchen' : 'Ausgebucht'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Plätze gefunden</h3>
              <p className="text-muted-foreground">Versuche einen anderen Suchbegriff oder Datum</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
